import { computed, reactive } from "vue";
import { useStore } from "vuex";
import {
  ANNOTATION_COLOR,
  ANNOTATION_COLOR_ARRAY,
  ANNOTATION_SHAPE,
  ANNOTATION_SHAPE_ARRAY,
  ANNOTATION_TYPE,
  ASSESSMENT_SELECTOR,
  ASSESSMENT_SELECTOR_ARRAY,
} from "../service/annotation.constant";
import DOMUtils from "@utils/dom.utils";
import { NAMESPACE } from "@modules/annotation/service/annotation.constant";
import { Annotation, AnnotationAttemptAdapter } from "@modules/annotation/types.ts";
import { isEmpty } from "@utils/var.check";
import { ApiResponse } from "@apis/api.ts";
import { MediaObjectMetadataModel, UserGenerateContentPreviewModel } from "@plugins/media/types.ts";
import { AnnotationEventBus } from "@modules/annotation/service/annotation.event.bus.ts";
import annotationApi from "@apis/annotation.api.ts";

export function useAnnotation(anchor?: string, adapter?: AnnotationAttemptAdapter) {
  const store = useStore();
  const adapterCurrentAnchor = computed(() => adapter?.currentAnchor.value ?? "");
  const currentUserId = computed(() => adapter?.currentUserId.value ?? "");
  const isStudentStudentView = computed(() => adapter?.isStudentStudentView.value ?? false);
  const isTeacherStudentView = computed(() => adapter?.isTeacherStudentView.value ?? false);
  const isPreviewMode = computed(() => adapter?.isPreviewMode.value ?? false);

  const getAnnotation = (uuid: string) =>
    store.getters[`${NAMESPACE.ANNOTATION}/getAnnotation`](uuid);

  const fetchAnnotation = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/fetchAnnotation`, payload);
  const createAnnotation = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/createAnnotation`, payload);
  const deleteAnnotation = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/deleteAnnotation`, payload);
  const removeAnnotationsByComponentId = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/removeAnnotationsByComponentId`, payload);
  const createDraftAnnotation = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/createDraftAnnotation`, payload);
  const updateAnnotationContent = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/updateAnnotationContent`, payload);
  const setWithNote = (payload) => store.dispatch(`${NAMESPACE.ANNOTATION}/setWithNote`, payload);
  const setDictionaryPopupState = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/setDictionaryPopupState`, payload);
  const setAnnotationPopupState = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/setAnnotationPopupState`, payload);
  const addAnnotationToolbarIds = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/addAnnotationToolbarIds`, payload);
  const removeAnnotationToolbarIds = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/removeAnnotationToolbarIds`, payload);
  const setSelectedHighlight = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/setSelectedHighlight`, payload);
  const setSelectedAnnotation = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/setSelectedAnnotation`, payload);
  const setLastCreatedImageAnnotation = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/setLastCreatedImageAnnotation`, payload);
  const setActiveImageAnnotation = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/setActiveImageAnnotation`, payload);
  const toggleAnnotationComment = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/toggleAnnotationComment`, payload);
  const updateAnnotationAccordionsState = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/updateAnnotationAccordionsState`, payload);
  const updateActivityNoteAccordionState = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/updateActivityNoteAccordionState`, payload);
  const setAnnotationAccordionState = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/setAnnotationAccordionState`, payload);
  const addAnnotation = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/addAnnotation`, payload);

  const state = reactive({
    isShowToolbar: false,
  });

  // Vuex state and getters
  const annotationsByUuid = computed<Record<string, Annotation>>(
    () => store.state.annotation.annotationsByUuid,
  );
  const listLoaded = computed<Record<string, Annotation>>(() => store.state.annotation.listLoaded);

  const currentAnchor = computed(() => {
    return anchor ?? adapterCurrentAnchor.value;
  });
  const userId = computed(() => store.getters[`${NAMESPACE.AUTH}/userId`]);
  const isTeacher = computed(() => store.getters[`${NAMESPACE.AUTH}/isTeacher`]);
  const isStudent = computed(() => store.getters[`${NAMESPACE.AUTH}/isStudent`]);
  const getAnnotationToolbarIds = computed(
    () => store.getters[`${NAMESPACE.ANNOTATION}/getAnnotationToolbarIds`],
  );
  const getSelectedHighlight = computed(
    () => store.getters[`${NAMESPACE.ANNOTATION}/getSelectedHighlight`],
  );
  const getSelectedAnnotation = computed(
    () => store.getters[`${NAMESPACE.ANNOTATION}/getSelectedAnnotation`],
  );
  const getSortedAnnotationList = computed(
    () => store.getters[`${NAMESPACE.ANNOTATION}/getSortedAnnotationList`],
  );
  const getAnnotationAccordionState = computed(
    () => store.getters[`${NAMESPACE.ANNOTATION}/getAnnotationAccordionState`],
  );

  const getSortedActivityNoteList = computed(
    () => store.getters[`${NAMESPACE.ANNOTATION}/getSortedActivityNoteList`],
  );

  const isAnnotationPopupOpened = computed(
    () => store.getters[`${NAMESPACE.ANNOTATION}/isAnnotationPopupOpened`],
  );

  const isDictionaryPopupOpened = computed(
    () => store.getters[`${NAMESPACE.ANNOTATION}/isDictionaryPopupOpened`],
  );

  const annotations = computed(() => {
    return (Object.values(annotationsByUuid.value) as Annotation[]).filter((a) => {
      return (isEmpty(currentAnchor.value) || a.anchor === currentAnchor.value) && !a.outdated;
    });
  });

  const isShowHighlightToolbar = computed(() => state.isShowToolbar);
  const isTeacherView = computed(() => isTeacher.value);
  const isStudentView = computed(() => isStudent.value);

  const isOwnView = computed(() => userId.value === currentUserId.value);
  const hasAnnotation = computed(() => Object.keys(annotationsByUuid.value).length > 0);

  const getWithNote = computed(() => store.getters[`${NAMESPACE.ANNOTATION}/getWithNote`]);

  const getToolbarConfig = (type) => {
    switch (type) {
      case ANNOTATION_TYPE.TEXT_ANNOTATION:
        return isStudentView.value
          ? [
              { color: ANNOTATION_COLOR.highlight1, shape: ANNOTATION_SHAPE.shape2 },
              { color: ANNOTATION_COLOR.highlight2, shape: ANNOTATION_SHAPE.shape2 },
              { color: ANNOTATION_COLOR.highlight3, shape: ANNOTATION_SHAPE.shape2 },
            ]
          : [
              { color: ASSESSMENT_SELECTOR.assess1, shape: ANNOTATION_SHAPE.shape2 },
              { color: ASSESSMENT_SELECTOR.assess2, shape: ANNOTATION_SHAPE.shape2 },
              { color: ASSESSMENT_SELECTOR.assess3, shape: ANNOTATION_SHAPE.shape2 },
            ];
      case ANNOTATION_TYPE.IMAGE_ANNOTATION:
        return isStudentView.value
          ? [
              { color: ANNOTATION_COLOR.highlight1, shape: ANNOTATION_SHAPE.shape1 },
              { color: ANNOTATION_COLOR.highlight2, shape: ANNOTATION_SHAPE.shape2 },
              { color: ANNOTATION_COLOR.highlight3, shape: ANNOTATION_SHAPE.shape3 },
            ]
          : [
              { color: ASSESSMENT_SELECTOR.assess1, shape: ANNOTATION_SHAPE.shape1 },
              { color: ASSESSMENT_SELECTOR.assess2, shape: ANNOTATION_SHAPE.shape2 },
              { color: ASSESSMENT_SELECTOR.assess3, shape: ANNOTATION_SHAPE.shape3 },
            ];
      default:
        return [];
    }
  };

  const showHighlightToolbar = (isPreviousComponent) => {
    state.isShowToolbar = true;
    if (isPreviousComponent) {
      store.dispatch(`${NAMESPACE.ANNOTATION}/removeAnnotationToolbarIds`, currentAnchor.value);
    } else if (!getAnnotationToolbarIds.value.includes(currentAnchor.value)) {
      store.dispatch(`${NAMESPACE.ANNOTATION}/addAnnotationToolbarIds`, currentAnchor.value);
    }
  };

  const hideHighlightToolbar = () => {
    state.isShowToolbar = false;
  };

  const handleExpandAnnotationAccordion = (annotationCard, isActivityNote = false) => {
    const annotationAccordion = DOMUtils.parent(annotationCard, ".bx--accordion");
    if (!isActivityNote && !annotationAccordion) {
      return;
    }

    const questionId = isActivityNote ? "0" : annotationAccordion.id.split("-").slice(3).join("-");
    store.dispatch(`${NAMESPACE.ANNOTATION}/setAnnotationAccordionState`, {
      questionId,
      state: true,
    });
  };

  const handleExpandAnnotationAccordionByAccordionId = (accordionId) => {
    store.dispatch(`${NAMESPACE.ANNOTATION}/setAnnotationAccordionState`, {
      questionId: accordionId,
      state: true,
    });
  };

  const handleToggleComment = (annotationUuid, status) => {
    store.dispatch(`${NAMESPACE.ANNOTATION}/toggleAnnotationComment`, {
      annotationUuid,
      toggleStatus: status,
    });
  };

  const getBaseHighlighterClass = (selector) =>
    selector.includes("assess") ? "assess" : "highlight";

  const getDefaultColor = () =>
    isTeacher.value ? ASSESSMENT_SELECTOR_ARRAY[0] : ANNOTATION_COLOR_ARRAY[0];

  const getColorSelector = (classList) => {
    for (const c of classList) {
      if (isTeacher.value && ASSESSMENT_SELECTOR_ARRAY.includes(c)) return c;
      if (!isTeacher.value && ANNOTATION_COLOR_ARRAY.includes(c)) return c;
    }
    return "";
  };

  const getColorShapeSelector = (classList) => {
    let color = isTeacher.value ? "assess" : "";
    let shape = "";
    for (const c of classList) {
      if (isTeacher.value && ASSESSMENT_SELECTOR_ARRAY.includes(c)) shape = c;
      else if (!isTeacher.value && ANNOTATION_COLOR_ARRAY.includes(c)) color = c;
      else if (!isTeacher.value && ANNOTATION_SHAPE_ARRAY.includes(c)) shape = c;
    }
    return `${color} ${shape}`.trim();
  };

  const loadTimestampAnnotation = (annotation, isFromSubpage = false) => {
    const annotationList: object[] = [];
    annotationList.push({
      timestamp: annotation.data.highlighterSelectors[0].start,
      highlighterId: annotation.data.highlighterId,
      uuid: annotation.uuid,
      highlighterClass: annotation.data.highlighterClass,
    });

    AnnotationEventBus.$emit("renderTimestampAnnotation", {
      annotationList: annotationList,
      anchor: annotation.anchor,
      uuid: annotation.uuid,
      isFromSubpage: isFromSubpage,
    });
  };

  const getUgcByAnnotation = (annotation): Promise<UserGenerateContentPreviewModel> => {
    return new Promise((resolve, reject) => {
      annotationApi.fetchUgcByAnchor(annotation.uuid).then(
        (response: ApiResponse<UserGenerateContentPreviewModel>) => {
          if (!response || response.code === "error") {
            reject();
            return;
          }
          resolve(response.data);
        },
        () => {
          reject();
        },
      );
    });
  };

  const getMediaByAnnotation = (annotation): Promise<MediaObjectMetadataModel> => {
    return new Promise((resolve, reject) => {
      annotationApi.fetchMediaObjectByAnchor(annotation.uuid).then(
        (response: ApiResponse<MediaObjectMetadataModel>) => {
          if (!response || response.code === "error") {
            reject();
            return;
          }
          resolve(response.data);
        },
        () => {
          reject();
        },
      );
    });
  };

  return {
    annotationsByUuid,
    listLoaded,
    getWithNote,
    getAnnotation,
    getSelectedAnnotation,
    getAnnotationAccordionState,
    getSelectedHighlight,
    getSortedAnnotationList,
    getSortedActivityNoteList,
    fetchAnnotation,
    createAnnotation,
    deleteAnnotation,
    removeAnnotationsByComponentId,
    updateAnnotationContent,
    createDraftAnnotation,
    setWithNote,
    setDictionaryPopupState,
    setAnnotationPopupState,
    addAnnotationToolbarIds,
    removeAnnotationToolbarIds,
    setSelectedHighlight,
    setSelectedAnnotation,
    setLastCreatedImageAnnotation,
    setActiveImageAnnotation,
    toggleAnnotationComment,
    updateAnnotationAccordionsState,
    updateActivityNoteAccordionState,
    setAnnotationAccordionState,
    addAnnotation,
    loadTimestampAnnotation,
    annotations,
    isShowHighlightToolbar,
    isDictionaryPopupOpened,
    isAnnotationPopupOpened,
    isTeacherView,
    isStudentView,
    isStudentStudentView,
    isTeacherStudentView,
    isPreviewMode,
    isOwnView,
    userId,
    hasAnnotation,
    getToolbarConfig,
    showHighlightToolbar,
    hideHighlightToolbar,
    handleExpandAnnotationAccordion,
    handleExpandAnnotationAccordionByAccordionId,
    handleToggleComment,
    getBaseHighlighterClass,
    getDefaultColor,
    getColorSelector,
    getColorShapeSelector,
    getMediaByAnnotation,
    getUgcByAnnotation,
  };
}
